import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
    Users, AlertTriangle, Plus, Pencil, Trash2, Shield, Search,
    Calendar, Clock, LogOut, Store, User, Loader2, X, Trash, Layers, Power, PowerOff, CheckCircle2
} from 'lucide-react';

const PLANS = [
    { value: 'starter', label: 'Starter', color: 'bg-blue-100 text-blue-700' },
    { value: 'pro', label: 'Pro', color: 'bg-purple-100 text-purple-700' },
    { value: 'premium', label: 'Premium', color: 'bg-amber-100 text-amber-800' },
];

function PlanBadge({ plan }) {
    const p = PLANS.find(pl => pl.value === plan) || { label: plan || '-', color: 'bg-slate-100 text-slate-600' };
    return <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${p.color}`}>{p.label}</span>;
}

function AlertBanner({ alerts }) {
    if (!alerts || alerts.length === 0) return null;
    const expired = alerts.filter(a => a.expired);
    const expiring = alerts.filter(a => !a.expired);
    return (
        <div className="space-y-2">
            {expired.map(a => (
                <div key={a.user_id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid={`alert-expired-${a.user_id}`}>
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-red-800">{a.business_name || a.name}</span>
                        <span className="text-xs text-red-600 ml-2">Forfait expiré depuis {Math.abs(a.days_left)} jour(s)</span>
                    </div>
                    <PlanBadge plan={a.plan} />
                </div>
            ))}
            {expiring.map(a => (
                <div key={a.user_id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`alert-expiring-${a.user_id}`}>
                    <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-amber-800">{a.business_name || a.name}</span>
                        <span className="text-xs text-amber-600 ml-2">Expire dans {a.days_left} jour(s)</span>
                    </div>
                    <PlanBadge plan={a.plan} />
                </div>
            ))}
        </div>
    );
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState('merchants');
    const [programStatusFilter, setProgramStatusFilter] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [merchantPrograms, setMerchantPrograms] = useState(null); // {merchant, programs} drill-down
    const [toggleTarget, setToggleTarget] = useState(null); // program object being toggled
    const [toggleReason, setToggleReason] = useState('');
    const [toggling, setToggling] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'merchant', business_name: '', plan: 'starter', plan_start: '', plan_end: '' });
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [usersRes, alertsRes, programsRes] = await Promise.all([
                api.get('/admin/users'),
                api.get('/admin/alerts'),
                api.get('/admin/cards'),
            ]);
            setUsers(usersRes.data);
            setAlerts(alertsRes.data);
            setPrograms(programsRes.data);
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('Accès non autorisé');
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filteredUsers = users.filter(u => {
        const matchTab = tab === 'merchants' ? u.role === 'merchant' : u.role === 'client';
        const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()) || u.business_name?.toLowerCase().includes(search.toLowerCase());
        return matchTab && matchSearch;
    });

    const filteredPrograms = programs.filter(p => {
        const matchSearch = !search ||
            p.name?.toLowerCase().includes(search.toLowerCase()) ||
            p.merchant_business_name?.toLowerCase().includes(search.toLowerCase()) ||
            p.merchant_email?.toLowerCase().includes(search.toLowerCase());
        const matchStatus =
            programStatusFilter === 'all' ||
            (programStatusFilter === 'active' && !p.admin_disabled) ||
            (programStatusFilter === 'disabled' && p.admin_disabled);
        return matchSearch && matchStatus;
    });

    const openToggle = (program) => {
        setToggleTarget(program);
        setToggleReason(program.admin_disabled_reason || '');
    };

    const confirmToggle = async () => {
        if (!toggleTarget) return;
        setToggling(true);
        try {
            const willDisable = !toggleTarget.admin_disabled;
            await api.patch(`/admin/cards/${toggleTarget.id}/toggle`, {
                disabled: willDisable,
                reason: willDisable ? toggleReason.trim() : '',
            });
            toast.success(willDisable ? 'Programme désactivé' : 'Programme réactivé');
            setToggleTarget(null);
            setToggleReason('');
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setToggling(false);
        }
    };

    const openMerchantPrograms = (merchant) => {
        const merchantProgs = programs.filter(p => p.merchant_id === merchant.id);
        setMerchantPrograms({ merchant, programs: merchantProgs });
    };

    const openCreate = () => {
        setEditUser(null);
        setFormData({ name: '', email: '', password: '', role: tab === 'merchants' ? 'merchant' : 'client', business_name: '', plan: 'starter', plan_start: '', plan_end: '' });
        setShowForm(true);
    };

    const openEdit = (u) => {
        setEditUser(u);
        setFormData({
            name: u.name || '', email: u.email || '', password: '', role: u.role || 'merchant',
            business_name: u.business_name || '', plan: u.plan || 'starter',
            plan_start: u.plan_start ? u.plan_start.split('T')[0] : '',
            plan_end: u.plan_end ? u.plan_end.split('T')[0] : '',
        });
        setShowForm(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { ...formData };
            if (payload.plan_start) payload.plan_start = new Date(payload.plan_start).toISOString();
            if (payload.plan_end) payload.plan_end = new Date(payload.plan_end).toISOString();
            if (!payload.password) delete payload.password;

            if (editUser) {
                await api.put(`/admin/users/${editUser.id}`, payload);
                toast.success('Utilisateur mis à jour');
            } else {
                if (!payload.password) payload.password = 'Fidelrun2024!';
                await api.post('/admin/users', payload);
                toast.success('Utilisateur créé');
            }
            setShowForm(false);
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteUser) return;
        try {
            await api.delete(`/admin/users/${deleteUser.id}`);
            toast.success('Utilisateur supprimé');
            setDeleteUser(null);
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleCleanup = async () => {
        if (!window.confirm('Supprimer TOUTES les données de test ? Cette action est irréversible.')) return;
        try {
            const res = await api.post('/admin/cleanup-test-data');
            toast.success(`Nettoyage terminé : ${JSON.stringify(res.data.deleted)}`);
            await fetchData();
        } catch (err) {
            toast.error('Erreur lors du nettoyage');
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    const merchantCount = users.filter(u => u.role === 'merchant').length;
    const clientCount = users.filter(u => u.role === 'client').length;

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-emerald-600" />
                        <h1 className="text-lg font-bold text-slate-900">Admin FidelRun</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleCleanup} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="cleanup-btn">
                            <Trash className="w-3.5 h-3.5 mr-1.5" /> Nettoyer test
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto p-4 space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                    <Card className="rounded-xl"><CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-slate-900">{merchantCount}</p>
                        <p className="text-xs text-slate-500">Commerçants</p>
                    </CardContent></Card>
                    <Card className="rounded-xl"><CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-slate-900">{clientCount}</p>
                        <p className="text-xs text-slate-500">Clients</p>
                    </CardContent></Card>
                    <Card className="rounded-xl"><CardContent className="p-4 text-center">
                        <p className={`text-2xl font-bold ${alerts.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{alerts.length}</p>
                        <p className="text-xs text-slate-500">Alertes</p>
                    </CardContent></Card>
                </div>

                {/* Alerts */}
                <AlertBanner alerts={alerts} />

                {/* Tabs + Search */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 flex-wrap">
                        <button onClick={() => setTab('merchants')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${tab === 'merchants' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="tab-merchants">
                            <Store className="w-3.5 h-3.5 inline mr-1.5" />Commerçants
                        </button>
                        <button onClick={() => setTab('clients')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${tab === 'clients' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="tab-clients">
                            <User className="w-3.5 h-3.5 inline mr-1.5" />Clients
                        </button>
                        <button onClick={() => setTab('programs')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${tab === 'programs' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="tab-programs">
                            <Layers className="w-3.5 h-3.5 inline mr-1.5" />Programmes
                            {programs.filter(p => p.admin_disabled).length > 0 && (
                                <span className="ml-1.5 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">{programs.filter(p => p.admin_disabled).length}</span>
                            )}
                        </button>
                    </div>
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-users" />
                    </div>
                    {tab === 'programs' ? (
                        <div className="flex bg-white border border-slate-200 rounded-lg p-0.5">
                            {[
                                { v: 'all', l: 'Tous' },
                                { v: 'active', l: 'Actifs' },
                                { v: 'disabled', l: 'Désactivés' },
                            ].map(s => (
                                <button key={s.v} onClick={() => setProgramStatusFilter(s.v)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${programStatusFilter === s.v ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`program-filter-${s.v}`}>
                                    {s.l}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-600 text-white h-9" data-testid="create-user-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> {tab === 'merchants' ? 'Commerçant' : 'Client'}
                        </Button>
                    )}
                </div>

                {/* Users Table */}
                {tab !== 'programs' && (
                <Card className="rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="users-table">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50">
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Nom</th>
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Email</th>
                                    {tab === 'merchants' && <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Entreprise</th>}
                                    {tab === 'merchants' && <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Forfait</th>}
                                    {tab === 'merchants' && <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Expiration</th>}
                                    <th className="text-right text-xs font-medium text-slate-500 px-4 py-2.5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(u => {
                                    const isExpired = u.plan_end && new Date(u.plan_end) < new Date();
                                    const isExpiring = u.plan_end && !isExpired && (new Date(u.plan_end) - new Date()) < 7 * 86400000;
                                    return (
                                        <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${u.id}`}>
                                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{u.name}</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                                            {tab === 'merchants' && <td className="px-4 py-3 text-sm text-slate-600">{u.business_name}</td>}
                                            {tab === 'merchants' && <td className="px-4 py-3"><PlanBadge plan={u.plan} /></td>}
                                            {tab === 'merchants' && (
                                                <td className="px-4 py-3">
                                                    {u.plan_end ? (
                                                        <span className={`text-xs font-medium ${isExpired ? 'text-red-500' : isExpiring ? 'text-amber-500' : 'text-slate-500'}`}>
                                                            {isExpired ? 'Expiré' : new Date(u.plan_end).toLocaleDateString('fr-FR')}
                                                        </span>
                                                    ) : <span className="text-xs text-slate-300">-</span>}
                                                </td>
                                            )}
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {tab === 'merchants' && (
                                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openMerchantPrograms(u)} title="Voir les programmes" data-testid={`view-programs-${u.id}`}>
                                                            <Layers className="w-3.5 h-3.5 text-emerald-500" />
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                                                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                                    </Button>
                                                    {u.email !== 'amiriaz2000@gmail.com' && (
                                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-8">Aucun utilisateur trouvé</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
                )}

                {/* Programs Table */}
                {tab === 'programs' && (
                <Card className="rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="programs-table">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50">
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Commerçant</th>
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Programme</th>
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Type</th>
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Clients</th>
                                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-2.5">Statut</th>
                                    <th className="text-right text-xs font-medium text-slate-500 px-4 py-2.5">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPrograms.map(p => (
                                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`program-row-${p.id}`}>
                                        <td className="px-4 py-3">
                                            <p className="text-sm font-medium text-slate-900">{p.merchant_business_name || '-'}</p>
                                            <p className="text-xs text-slate-400">{p.merchant_email}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-6 rounded-sm shrink-0" style={{ background: `linear-gradient(180deg, ${p.card_color_from || '#10b981'}, ${p.card_color_to || '#059669'})` }} />
                                                <span className="text-sm font-medium text-slate-800">{p.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs font-medium text-slate-600 uppercase">{p.type || '-'}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">{p.customer_count}</td>
                                        <td className="px-4 py-3">
                                            {p.admin_disabled ? (
                                                <div>
                                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                                        <PowerOff className="w-3 h-3" /> Désactivé
                                                    </span>
                                                    {p.admin_disabled_reason && (
                                                        <p className="text-[10px] text-slate-400 mt-1 max-w-[200px] truncate" title={p.admin_disabled_reason}>{p.admin_disabled_reason}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                                    <CheckCircle2 className="w-3 h-3" /> Actif
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                variant={p.admin_disabled ? 'outline' : 'ghost'}
                                                size="sm"
                                                className={p.admin_disabled ? 'text-emerald-600 border-emerald-200 hover:bg-emerald-50' : 'text-red-500 hover:bg-red-50'}
                                                onClick={() => openToggle(p)}
                                                data-testid={`toggle-program-${p.id}`}
                                            >
                                                {p.admin_disabled ? <><Power className="w-3.5 h-3.5 mr-1.5" />Réactiver</> : <><PowerOff className="w-3.5 h-3.5 mr-1.5" />Désactiver</>}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredPrograms.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-8">Aucun programme trouvé</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
                )}
            </main>

            {/* Create/Edit Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="rounded-2xl sm:max-w-md" data-testid="user-form-dialog">
                    <DialogHeader>
                        <DialogTitle>{editUser ? 'Modifier' : 'Créer'} un {formData.role === 'merchant' ? 'commerçant' : 'client'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Nom</Label>
                            <Input value={formData.name} onChange={e => setFormData(f => ({...f, name: e.target.value}))} data-testid="form-name" />
                        </div>
                        <div>
                            <Label className="text-xs">Email</Label>
                            <Input type="email" value={formData.email} onChange={e => setFormData(f => ({...f, email: e.target.value}))} data-testid="form-email" />
                        </div>
                        <div>
                            <Label className="text-xs">Mot de passe {editUser && '(laisser vide pour ne pas changer)'}</Label>
                            <Input type="password" value={formData.password} onChange={e => setFormData(f => ({...f, password: e.target.value}))} data-testid="form-password" />
                        </div>
                        {formData.role === 'merchant' && (
                            <>
                                <div>
                                    <Label className="text-xs">Nom de l'entreprise</Label>
                                    <Input value={formData.business_name} onChange={e => setFormData(f => ({...f, business_name: e.target.value}))} data-testid="form-business" />
                                </div>
                                <div>
                                    <Label className="text-xs">Forfait</Label>
                                    <div className="flex gap-2 mt-1">
                                        {PLANS.map(p => (
                                            <button key={p.value} onClick={() => setFormData(f => ({...f, plan: p.value}))}
                                                className={`flex-1 py-2 text-sm font-medium rounded-lg border-2 transition-all ${formData.plan === p.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                                                data-testid={`plan-${p.value}`}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Début forfait</Label>
                                        <Input type="date" value={formData.plan_start} onChange={e => setFormData(f => ({...f, plan_start: e.target.value}))} data-testid="form-plan-start" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Fin forfait</Label>
                                        <Input type="date" value={formData.plan_end} onChange={e => setFormData(f => ({...f, plan_end: e.target.value}))} data-testid="form-plan-end" />
                                    </div>
                                </div>
                            </>
                        )}
                        <Button onClick={handleSave} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white mt-2" data-testid="save-user-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editUser ? 'Mettre à jour' : 'Créer')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
                <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="delete-user-dialog">
                    <DialogHeader>
                        <DialogTitle>Supprimer l'utilisateur</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">
                        Supprimer <strong>{deleteUser?.name || deleteUser?.email}</strong> ? Toutes ses données seront perdues.
                    </p>
                    <div className="flex gap-3 justify-end pt-2">
                        <Button variant="outline" size="sm" onClick={() => setDeleteUser(null)}>Annuler</Button>
                        <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="confirm-delete-btn">
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Toggle Program Dialog (disable/enable with reason) */}
            <Dialog open={!!toggleTarget} onOpenChange={(o) => { if (!o) { setToggleTarget(null); setToggleReason(''); } }}>
                <DialogContent className="rounded-2xl sm:max-w-md" data-testid="toggle-program-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {toggleTarget?.admin_disabled ? 'Réactiver le programme' : 'Désactiver le programme'}
                        </DialogTitle>
                    </DialogHeader>
                    {toggleTarget && (
                        <div className="space-y-3">
                            <div className="bg-slate-50 rounded-xl p-3">
                                <p className="text-xs text-slate-500">Programme</p>
                                <p className="text-sm font-semibold text-slate-900">{toggleTarget.name}</p>
                                <p className="text-xs text-slate-500 mt-1">{toggleTarget.merchant_business_name} · {toggleTarget.customer_count} client(s)</p>
                            </div>
                            {!toggleTarget.admin_disabled && (
                                <div>
                                    <Label className="text-xs">Motif (optionnel — visible côté commerçant)</Label>
                                    <Input
                                        value={toggleReason}
                                        onChange={(e) => setToggleReason(e.target.value)}
                                        placeholder="Ex: Non-respect des CGU, fraude détectée..."
                                        maxLength={200}
                                        data-testid="toggle-reason-input"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">Les clients verront « Programme temporairement indisponible ». Le scan et l'attribution de points seront bloqués.</p>
                                </div>
                            )}
                            {toggleTarget.admin_disabled && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                                    Le programme sera réactivé immédiatement et redeviendra utilisable par les clients.
                                </div>
                            )}
                            <div className="flex gap-2 justify-end pt-1">
                                <Button variant="outline" size="sm" onClick={() => { setToggleTarget(null); setToggleReason(''); }}>Annuler</Button>
                                <Button
                                    size="sm"
                                    onClick={confirmToggle}
                                    disabled={toggling}
                                    className={toggleTarget.admin_disabled ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}
                                    data-testid="confirm-toggle-btn"
                                >
                                    {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : (toggleTarget.admin_disabled ? 'Réactiver' : 'Désactiver')}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Merchant Programs Drill-down Dialog */}
            <Dialog open={!!merchantPrograms} onOpenChange={(o) => { if (!o) setMerchantPrograms(null); }}>
                <DialogContent className="rounded-2xl sm:max-w-2xl" data-testid="merchant-programs-dialog">
                    <DialogHeader>
                        <DialogTitle>Programmes de {merchantPrograms?.merchant?.business_name || merchantPrograms?.merchant?.name}</DialogTitle>
                    </DialogHeader>
                    {merchantPrograms && (
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {merchantPrograms.programs.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-6">Ce commerçant n'a aucun programme.</p>
                            ) : merchantPrograms.programs.map(p => (
                                <div key={p.id} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`drill-program-${p.id}`}>
                                    <div className="w-2 h-10 rounded-sm shrink-0" style={{ background: `linear-gradient(180deg, ${p.card_color_from || '#10b981'}, ${p.card_color_to || '#059669'})` }} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                                        <p className="text-xs text-slate-500">{(p.type || '').toUpperCase()} · {p.customer_count} client(s)</p>
                                        {p.admin_disabled && p.admin_disabled_reason && (
                                            <p className="text-[11px] text-red-500 mt-0.5 truncate">Motif : {p.admin_disabled_reason}</p>
                                        )}
                                    </div>
                                    {p.admin_disabled ? (
                                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 shrink-0">Désactivé</span>
                                    ) : (
                                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">Actif</span>
                                    )}
                                    <Button
                                        variant={p.admin_disabled ? 'outline' : 'ghost'}
                                        size="sm"
                                        className={p.admin_disabled ? 'text-emerald-600 border-emerald-200 hover:bg-emerald-50 shrink-0' : 'text-red-500 hover:bg-red-50 shrink-0'}
                                        onClick={() => { setMerchantPrograms(null); openToggle(p); }}
                                        data-testid={`drill-toggle-${p.id}`}
                                    >
                                        {p.admin_disabled ? 'Réactiver' : 'Désactiver'}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
